/**
 * Checkout Mixin для Vue компонентів
 * Надає методи для відстеження початку оформлення замовлення
 */
import CheckoutTracker from '@/utils/checkoutTracker';
export default {
    data() {
        return {
            checkoutTracker: null
        };
    },
    methods: {
        /**
         * Ініціалізує трекер початку замовлення
         */
        initCheckoutTracker() {
            if (!this.checkoutTracker) {
                this.checkoutTracker = new CheckoutTracker();
                // Делаем checkoutTracker доступным глобально для других миксинов
                window.checkoutTracker = this.checkoutTracker;
            }
        },
        /**
         * Відстежує початок оформлення замовлення
         * @param {Array} items - Масив товарів (опціонально)
         * @param {boolean} force - Примусово відправити, ігноруючи захист від дублювання
         */
        async trackBeginCheckout(items = null, force = false) {
            // Встановлюємо глобальний флаг, щоб автоматичний трекер не спрацьовував
            window.checkoutManualTriggered = true;
            setTimeout(() => {
                window.checkoutManualTriggered = false;
            }, 2000); // Скидаємо флаг через 2 секунди
            // Инициализируем checkoutTracker если его нет
            if (!this.checkoutTracker) {
                this.initCheckoutTracker();
            }
            // Убеждаемся, что checkoutTracker доступен глобально
            if (this.checkoutTracker && !window.checkoutTracker) {
                window.checkoutTracker = this.checkoutTracker;
            }
            // Якщо товари не передані, спробуємо отримати з кошика
            if (!items) {
                items = this.getBasketItemsForCheckout();
            }
            if (items && items.length > 0) {
                // Товары уже отформатированы в getBasketItemsForCheckout
                await this.checkoutTracker.trackBeginCheckout(items, force);
            } else {
                // Передаем store напрямую в трекер
                await this.checkoutTracker.trackBeginCheckoutWithStore(null, force, this.$store);
            }
        },
        /**
         * Отримує товари з кошика для відстеження
         * @returns {Array} Масив товарів
         */
        getBasketItemsForCheckout() {
            // Инициализируем checkoutTracker если его нет
            if (!this.checkoutTracker) {
                this.initCheckoutTracker();
            }
            // ПРИОРИТЕТ 1: Используем все товары из корзины
            if (this.$store) {
                const basket = this.$store.getters['basket/basket'];
                const secondBasket = this.$store.getters['basket/secondBasket'];
                // Проверяем основную корзину
                if (basket && Array.isArray(basket) && basket.length > 0) {
                    // Форматируем товары из корзины перед возвратом
                    return this.formatBasketItemsForCheckout(basket);
                } else if (secondBasket && Array.isArray(secondBasket) && secondBasket.length > 0) {
                    // Форматируем товары из корзины перед возвратом
                    return this.formatBasketItemsForCheckout(secondBasket);
                }
                // ПРИОРИТЕТ 2: Если корзина пустая, используем текущий товар
                const currentProduct = this.$store.getters['catalog/currentProduct'];
                if (currentProduct && currentProduct.id && currentProduct.title) {
                    const formattedProduct = this.formatRawProductData(currentProduct);
                    if (formattedProduct) {
                        return [formattedProduct];
                    }
                } else {
                    return [];
                }
            }
            return [];
        },
        /**
         * Извлекает количество из DOM (как в checkoutTracker.js)
         * @returns {number}
         */
        extractQuantityFromDOM() {
            const quantitySelectors = [
                '.product__amount-wrapper input[type="number"]', // ПРИОРИТЕТ 1: basket-product компонент
                '.product__amount input[type="number"]', // ПРИОРИТЕТ 2: альтернативный селектор
                '.product__amount-wrapper .product__amount', // ПРИОРИТЕТ 3: span с количеством в wrapper
                '.product__amount', // ПРИОРИТЕТ 4: span с количеством
                '.product-amount', // Количество в компоненте заказа
                'input[type="number"]',
                '.quantity, [class*="quantity"]'
            ];
            for (const selector of quantitySelectors) {
                const quantityElement = document.querySelector(selector);
                if (quantityElement) {
                    if (quantityElement.type === 'number') {
                        // Берем актуальное значение из input поля
                        const quantity = parseInt(quantityElement.value) || parseInt(quantityElement.placeholder) || 1;
                        if (quantity > 0) return quantity;
                    } else {
                        const quantityText = quantityElement.textContent;
                        const quantityMatch = quantityText.match(/(\d+)/);
                        if (quantityMatch) {
                            const quantity = parseInt(quantityMatch[1]);
                            if (quantity > 0) return quantity;
                        }
                    }
                }
            }
            return 1;
        },
        /**
         * Нормалізує ідентифікатор до числового формату, якщо можливо (ідентично productViewTracker.js)
         * @param {any} id
         * @returns {number|string} Числовий ID або порожній рядок
         */
        normalizeId(id) {
            if (id === null || id === undefined) return '';
            const num = Number(id);
            if (!Number.isNaN(num) && Number.isFinite(num)) return num;
            return '' + id; // як фолбек рядок, щоб подія не впала
        },
        /**
         * Форматує сирі дані товару з store/глобальних змінних (ідентично productViewTracker.js)
         * @param {Object} product - Дані товару з store
         * @returns {Object}
         */
        formatRawProductData(product) {
            // Перевіряємо, чи це валідний об'єкт товару
            if (!product || typeof product !== 'object') {
                return null;
            }
            let productId = '';
            if (this.checkoutTracker && typeof this.checkoutTracker.extractProductId === 'function') {
                productId = this.checkoutTracker.extractProductId(product);
            } else {
                // Fallback: використовуємо product.id якщо checkoutTracker недоступний
                productId = product.id || product.product_price_id || product.productPriceId || '';
            }
            // Формуємо об'єкт з правильною структурою
            const formattedProduct = {
                id: this.normalizeId(productId) ?? productId,
                title: product.title || 'Невизначений товар',
                productPrice: product.productPrice || {
                    price: {price: 0},
                    baseUnit: {isM2: false, title: 'шт'}
                },
                category: product.category || {name: '', title: ''},
                productBrand: product.productBrand || {title: 'Невизначений бренд'}
            };
            // Додаткова перевірка ціни
            if (formattedProduct.productPrice && !formattedProduct.productPrice.price) {
                formattedProduct.productPrice.price = {price: 0};
            }
            if (formattedProduct.productPrice && !formattedProduct.productPrice.baseUnit) {
                formattedProduct.productPrice.baseUnit = {isM2: false, title: 'шт'};
            }
            return formattedProduct;
        },
        /**
         * Витягує бренд з заголовка товару (ідентично productViewTracker.js)
         * @param {string} title - Заголовок товару
         * @returns {string}
         */
        extractBrandFromTitle(title) {
            if (!title) return '';
            const parseBrandFromTitle = (title) => {
                const t = (title || '').trim();
                if (!t) return '';
                // Пріоритет 1: Шукаємо відомі бренди з модифікаторами (BauGlas FSX, RODECA, BAUGLAS)
                const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
                for (const brand of knownBrands) {
                    // Спочатку шукаємо бренд з можливими модифікаторами (FSX, Professional, тощо)
                    const brandWithModifier = t.match(new RegExp(`\\b${brand}\\s+([A-Z]{2,}(?:\\s+[A-Za-z]+)*)\\b`, 'i'));
                    if (brandWithModifier && brandWithModifier[0]) {
                        return brandWithModifier[0];
                    }
                    // Якщо не знайшли з модифікатором, шукаємо просто бренд
                    const brandMatch = t.match(new RegExp(`\\b${brand}\\b`, 'i'));
                    if (brandMatch && brandMatch[0]) {
                        return brandMatch[0];
                    }
                }
                // Додатковий пошук: шукаємо "BauGlas FSX" як окремий випадок
                const bauGlasFSXMatch = t.match(/\bBauGlas\s+FSX\b/i);
                if (bauGlasFSXMatch && bauGlasFSXMatch[0]) {
                    return bauGlasFSXMatch[0];
                }
                // Пріоритет 2: Шукаємо CamelCase (BauGlas, BauGlas FSX)
                const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
                if (camelMatch && camelMatch[0]) {
                    return camelMatch[0];
                }
                // Пріоритет 3: Шукаємо слова з великих літер (RODECA, BAUGLAS, FSX)
                const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
                if (capsMatch && capsMatch[0]) {
                    return capsMatch[0];
                }
                // Пріоритет 4: Спробуємо знайти бренд до коми
                const commaIdx = t.indexOf(',');
                if (commaIdx > 0) {
                    const beforeComma = t.slice(0, commaIdx).trim();
                    if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                        return beforeComma;
                    }
                }
                // Пріоритет 5: Беремо перше слово з 3+ літер
                const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
                if (firstWord && firstWord[0]) {
                    return firstWord[0];
                }
                return '';
            };
            return parseBrandFromTitle(title);
        },
        /**
         * Форматує товари з кошика для відстеження
         * @param {Array} basketProducts - Товари з кошика
         * @returns {Array} Відформатовані товари
         */
        formatBasketItemsForCheckout(basketProducts) {
            if (!basketProducts || !Array.isArray(basketProducts)) {
                return [];
            }
            // Ініціалізуємо checkoutTracker якщо він не ініціалізований
            if (!this.checkoutTracker) {
                this.initCheckoutTracker();
            }
            return basketProducts.map((item, index) => {
                // Для ordering компонента товари мають структуру item.select_type або item
                // Зберігаємо всю структуру для правильного визначення категорії
                const productData = {
                    ...item, // Зберігаємо всі дані з item
                    select_type: item.select_type,
                    product: item.product,
                    category: item.category || item.select_type?.category || item.product?.category
                };
                // Визначаємо назву товару з пріоритетом
                let title = productData.title || 
                           productData.select_type?.title || 
                           productData.product?.title || 
                           'Невідомий товар';
                // Визначаємо бренд з пріоритетом (ідентично productViewTracker.js):
                // 1. З даних товару (productBrand.title)
                // 2. З заголовка товару (евристика) - ОБОВ'ЯЗКОВО
                // 3. Фолбек
                let brand = productData.productBrand?.title || '';
                // ЗАВЖДИ намагаємося витягнути бренд з заголовка, навіть якщо є productBrand
                if (title) {
                    const extractedBrand = this.extractBrandFromTitle(title);
                    // Якщо витягли бренд з заголовка, використовуємо його
                    if (extractedBrand && extractedBrand.trim()) {
                        brand = extractedBrand;
                    }
                }
                if (!brand || !brand.trim()) {
                    brand = 'Невизначений бренд';
                }
                // Визначаємо категорію з пріоритетом:
                // 1. З даних товару (category.name або category.title)
                // 2. Збережена в sessionStorage (з попереднього кліку)
                // 3. Фолбек
                let categoryName = '';
                
                // Спочатку спробуємо отримати з даних товару
                if (productData.category) {
                    categoryName = productData.category.name || productData.category.title || '';
                }
                
                // Якщо не знайшли в даних товару, спробуємо sessionStorage
                if (!categoryName) {
                    try {
                        if (typeof window !== 'undefined' && window.sessionStorage) {
                            categoryName = sessionStorage.getItem('ga_last_item_category') || '';
                        }
                    } catch (e) { /* no-op */ }
                }
                
                if (!categoryName) {
                    categoryName = 'Невизначена категорія';
                }
                // ПРИОРИТЕТ 1: Ищем цену за единицу в данных товара (из store)
                let price = 0;
                if (productData.productPrice && productData.productPrice.price && productData.productPrice.price.price) {
                    price = parseFloat(productData.productPrice.price.price);
                } else if (productData.price) {
                    price = parseFloat(productData.price);
                } else if (productData.select_type && productData.select_type.price) {
                    price = parseFloat(productData.select_type.price);
                }
                
                // Если цена не найдена в данных, возвращаем 0
                if (isNaN(price) || price <= 0) {
                    price = 0;
                }
                // Визначаємо кількість
                let quantity = 1;
                if (item.quantity) {
                    quantity = parseInt(item.quantity);
                } else if (productData.quantity) {
                    quantity = parseInt(productData.quantity);
                } else {
                    // ПРИОРИТЕТ 3: Извлекаем количество из DOM (как в checkoutTracker.js)
                    quantity = this.extractQuantityFromDOM();
                }
                // Визначаємо ID товару - використовуємо extractProductId для отримання product_price_id (1044), а не product.id (360)
                let productId = '';
                if (this.checkoutTracker && typeof this.checkoutTracker.extractProductId === 'function') {
                    productId = this.checkoutTracker.extractProductId(productData);
                } else {
                    // Fallback: використовуємо productData.id якщо checkoutTracker недоступний
                    productId = productData.id || productData.product_price_id || productData.productPriceId || '';
                }
                const itemId = this.normalizeId(productId);
                const result = {
                    item_name: title,
                    item_id: itemId,
                    price: price,
                    currency: 'UAH',
                    item_brand: brand || '',
                    item_category: categoryName || '',
                    quantity: quantity
                };
                return result;
            });
        },
        /**
         * Відправляє дані початку замовлення в dataLayer
         * @param {Array} items - Масив товарів
         */
        pushCheckoutDataToDataLayer(items) {
            if (!window.dataLayer) {
                return;
            }
            // Отримуємо загальну вартість - ПРИОРИТЕТ для basket__total-wrapper-unit
            let totalValue = 0;
            // ПРИОРИТЕТ 1: Ищем в .basket__total-wrapper-unit (общая сумма корзины)
            const basketTotalElement = document.querySelector('.basket__total-wrapper-unit');
            if (basketTotalElement) {
                const totalPriceText = basketTotalElement.textContent?.trim() || '';
                // Парсимо ціну (прибираємо пробіли та замінюємо кому на крапку)
                const cleanPrice = totalPriceText.replace(/\s/g, '').replace(',', '.');
                const parsedPrice = parseFloat(cleanPrice);
                if (!isNaN(parsedPrice) && parsedPrice > 0) {
                    totalValue = parsedPrice;
                }
            }
            // ПРИОРИТЕТ 2: Ищем в .total-price h3 (ordering сторінка)
            if (totalValue === 0) {
                const totalPriceElement = document.querySelector('.total-price h3');
                if (totalPriceElement) {
                    const totalPriceText = totalPriceElement.textContent?.trim() || '';
                    // Парсимо ціну (прибираємо пробіли та замінюємо кому на крапку)
                    const cleanPrice = totalPriceText.replace(/\s/g, '').replace(',', '.');
                    const parsedPrice = parseFloat(cleanPrice);
                    if (!isNaN(parsedPrice) && parsedPrice > 0) {
                        totalValue = parsedPrice;
                    }
                }
            }
            // Fallback: обчислюємо загальну вартість з товарів, якщо не знайшли в DOM
            if (totalValue === 0) {
                totalValue = items.reduce((sum, item) => {
                    return sum + (parseFloat(item.price) * parseInt(item.quantity));
                }, 0);
            }
            // Очищуємо попередні дані ecommerce
            window.dataLayer.push({ecommerce: null});
            // Відправляємо подію з правильною структурою для GA4
            const eventData = {
                event: 'begin_checkout',
                ecommerce: {
                    currency: 'UAH', // Валюта на рівні ecommerce
                    items: items.map(item => ({
                        item_id: item.item_id,
                        item_name: item.item_name,
                        item_brand: item.item_brand,
                        item_category: item.item_category,
                        price: parseFloat(item.price),
                        currency: item.currency || 'UAH',
                        quantity: parseInt(item.quantity)
                    }))
                }
            };
            window.dataLayer.push(eventData);
        },
        /**
         * Отримує товари з DOM (basket-product компонентів)
         * @returns {Array}
         */
        getBasketItemsFromDOM() {
            const items = [];
            // Шукаємо товари в basket-product компонентах
            const productElements = document.querySelectorAll('.product');
            productElements.forEach(element => {
                const productData = this.extractProductDataFromBasketElement(element);
                if (productData) {
                    items.push(productData);
                }
            });
            // debug logs removed
            return items;
        },
        /**
         * Витягує дані товару з DOM елемента basket-product
         * @param {Element} element - Елемент товару
         * @returns {Object|null}
         */
        extractProductDataFromBasketElement(element) {
            // Витягуємо назву товару
            const titleElement = element.querySelector('.product__desc-name h4');
            if (!titleElement) return null;
            const title = titleElement.textContent?.trim() || '';
            if (!title) return null;
            // Витягуємо артикул
            const articleElement = element.querySelector('.product__desc-name-wrap h5');
            const article = articleElement?.textContent?.trim() || '';
            // Витягуємо ціну за одиницю - ПРИОРИТЕТ для .product__price h5 внутри конкретного элемента
            let price = 0;
            const priceSelectors = [
                '.product__price h5', // ПРИОРИТЕТ 1: цена за единицу товара в h5
                '.product__price', // ПРИОРИТЕТ 2: цена за единицу товара (fallback)
                '.product-price', // ПРИОРИТЕТ 3: цена товара
                '.price', // ПРИОРИТЕТ 4: общий селектор цены
                '[data-price]', // ПРИОРИТЕТ 5: data-атрибут
                '.cost, .amount' // ПРИОРИТЕТ 6: альтернативные селекторы
            ];
            
            for (const selector of priceSelectors) {
                const priceElement = element.querySelector(selector);
                if (priceElement) {
                    // Сначала проверяем data-атрибуты
                    const dataPrice = priceElement.getAttribute('data-price');
                    if (dataPrice) {
                        const parsedPrice = parseFloat(dataPrice);
                        if (!isNaN(parsedPrice) && parsedPrice > 0) {
                            price = parsedPrice;
                            break;
                        }
                    }
                    // Если data-атрибут не найден, парсим текст
                    const priceText = priceElement.textContent?.trim() || '';
                    if (priceText) {
                        // Парсим цену (убираем пробелы и заменяем запятую на точку)
                        const cleanPrice = priceText.replace(/\s/g, '').replace(',', '.');
                        const parsedPrice = parseFloat(cleanPrice);
                        if (!isNaN(parsedPrice) && parsedPrice > 0) {
                            price = parsedPrice;
                            break;
                        }
                    }
                }
            }
            // Витягуємо кількість - ПРИОРИТЕТ для product__amount-wrapper
            const quantityInput = element.querySelector('.product__amount-wrapper input[type="number"]') || 
                                 element.querySelector('input[type="number"]');
            let quantity = 1;
            if (quantityInput) {
                // Беремо актуальне значення з input поля
                quantity = parseInt(quantityInput.value) || parseInt(quantityInput.placeholder) || 1;
            }
            // totalPrice уже извлечен выше как price из product__total
            let totalPrice = price;
            // Витягуємо одиницю виміру
            const unitElement = element.querySelector('.product__unit');
            const unit = unitElement?.textContent?.trim() || 'шт';
            // Витягуємо зображення
            const imgElement = element.querySelector('.product__desc-img img');
            const image = imgElement?.src || '';
            // Витягуємо slug для посилання
            const linkElement = element.querySelector('.product__desc-img a');
            const slug = linkElement?.getAttribute('href')?.split('/').pop() || '';
            // Визначаємо бренд з заголовка
            const brand = this.extractBrandFromTitle(title);
            // Визначаємо категорію - поки що пуста (з DOM не можемо отримати)
            const categoryName = '';
            return {
                id: article || title, // Використовуємо артикул як ID, або назву як фолбек
                title: title,
                article: article,
                price: price, // Используем извлеченную цену
                quantity: quantity,
                totalPrice: price, // Используем извлеченную цену как общую цену
                unit: unit,
                image: image,
                slug: slug,
                // Формуємо структуру productPrice
                productPrice: {
                    price: {price: price},
                    baseUnit: {isM2: false, title: unit || 'шт'}
                },
                select_type: {
                    id: article || title,
                    title: title,
                    article: article,
                    price: price, // Используем извлеченную цену
                    unit: unit,
                    image: image,
                    slug: slug,
                    category: {
                        name: categoryName,
                        title: categoryName
                    }
                },
                productBrand: {
                    title: brand
                },
                category: {
                    name: categoryName,
                    title: categoryName
                }
            };
        },
        /**
         * Обробляє клік на кнопку оформлення замовлення в кошику
         */
        handleBasketCheckoutClick() {
            this.trackBeginCheckout();
        },
        /**
         * Оновлює кеш товарів кошика при зміні кількості
         */
        updateBasketCache() {
            if (this.checkoutTracker) {
                this.checkoutTracker.updateBasketCache();
            }
        }
    },
    beforeDestroy() {
        // Очищуємо трекер при знищенні компонента
        if (this.checkoutTracker) {
            this.checkoutTracker.destroy();
            this.checkoutTracker = null;
        }
    }
};